import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, UserPlus, Check, X, Clock, Loader2, Users } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { SectionHeader, EmptyState } from "@/components/Shared";
import { UserProfileCard } from "@/components/UserProfileCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

export default function Friends() {
  const { lang } = useI18n();
  const id = lang === "id";
  const qc = useQueryClient();
  const [tab, setTab] = useState("find");
  const [q, setQ] = useState("");
  const [cardId, setCardId] = useState(null);

  const { data: results } = useQuery({ queryKey: ["people", q], queryFn: async () => (await api.get("/people/search", { params: { q } })).data });
  const { data: requests } = useQuery({ queryKey: ["friend-requests"], queryFn: async () => (await api.get("/friends/requests")).data });
  const { data: friends } = useQuery({ queryKey: ["friends"], queryFn: async () => (await api.get("/friends")).data });

  const refetchAll = () => { qc.invalidateQueries({ queryKey: ["people"] }); qc.invalidateQueries({ queryKey: ["friend-requests"] }); qc.invalidateQueries({ queryKey: ["friends"] }); };

  const sendReq = async (u) => { try { await api.post("/friends/request", { to_id: u.user_id }); toast.success(id ? "Permintaan dikirim" : "Request sent"); refetchAll(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const respond = async (u, accept) => { try { await api.post("/friends/respond", { from_id: u.request_from || u.user_id, accept }); refetchAll(); } catch { toast.error("Error"); } };
  const unfriend = async (u) => { try { await api.post("/friends/remove", { user_id: u.user_id }); refetchAll(); } catch { toast.error("Error"); } };

  const StatusBtn = ({ u }) => {
    if (u.status === "friends") return <Button size="sm" variant="outline" onClick={() => unfriend(u)} data-testid={`unfriend-${u.user_id}`}>{id ? "Berteman" : "Friends"}</Button>;
    if (u.status === "outgoing") return <Button size="sm" variant="outline" disabled className="gap-1"><Clock className="h-3.5 w-3.5" /> {id ? "Terkirim" : "Sent"}</Button>;
    if (u.status === "incoming") return <Button size="sm" onClick={() => respond(u, true)} className="gap-1" data-testid={`accept-${u.user_id}`}><Check className="h-3.5 w-3.5" /> {id ? "Terima" : "Accept"}</Button>;
    return <Button size="sm" onClick={() => sendReq(u)} className="gap-1" data-testid={`add-friend-${u.user_id}`}><UserPlus className="h-3.5 w-3.5" /> {id ? "Tambah" : "Add"}</Button>;
  };

  const Row = ({ u, right }) => (
    <div className="flex items-center gap-3 rounded-xl border bg-card p-3" data-testid={`person-${u.user_id}`}>
      <button onClick={() => setCardId(u.user_id)} className="flex min-w-0 flex-1 items-center gap-3 text-left" data-testid={`open-profile-${u.user_id}`}>
        <Avatar className="h-10 w-10"><AvatarImage src={u.picture} className="object-cover" /><AvatarFallback className="bg-primary/15 text-sm font-semibold text-primary">{initials(u.name)}</AvatarFallback></Avatar>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium transition-colors hover:text-primary">{u.name}</div>
          {u.username && <div className="truncate text-xs text-primary">@{u.username}</div>}
          {u.gym_location && <div className="truncate text-xs text-muted-foreground">{u.gym_location}</div>}
        </div>
      </button>
      {right}
    </div>
  );

  const tabs = [["find", id ? "Cari Orang" : "Find People"], ["requests", id ? "Permintaan" : "Requests"], ["friends", id ? "Teman" : "Friends"]];

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <SectionHeader title={id ? "Teman" : "Friends"} subtitle={id ? "Terhubung dengan sesama member" : "Connect with fellow members"} />

      <div className="flex gap-2 rounded-xl border bg-card p-1">
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`friends-tab-${k}`}
            className={cn("relative flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors", tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent")}>
            {label}{k === "requests" && requests?.length ? ` (${requests.length})` : ""}
          </button>
        ))}
      </div>

      {tab === "find" && (
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={id ? "Cari nama, username, atau email..." : "Search name, username, or email..."} className="pl-9" data-testid="people-search" />
          </div>
          {(results || []).map((u) => <Row key={u.user_id} u={u} right={<StatusBtn u={u} />} />)}
          {results && !results.length && <p className="py-8 text-center text-sm text-muted-foreground">{id ? "Tidak ada hasil." : "No results."}</p>}
        </div>
      )}

      {tab === "requests" && (
        (requests?.length ? (
          <div className="space-y-3" data-testid="requests-list">
            {requests.map((u) => <Row key={u.user_id} u={u} right={
              <div className="flex gap-1.5">
                <Button size="sm" onClick={() => respond(u, true)} className="gap-1" data-testid={`accept-${u.request_from}`}><Check className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => respond(u, false)} data-testid={`decline-${u.request_from}`}><X className="h-3.5 w-3.5" /></Button>
              </div>
            } />)}
          </div>
        ) : <EmptyState icon={UserPlus} title={id ? "Permintaan" : "Requests"} description={id ? "Tidak ada permintaan pertemanan." : "No friend requests."} />)
      )}

      {tab === "friends" && (
        (friends?.length ? (
          <div className="space-y-3" data-testid="friends-list">
            {friends.map((u) => <Row key={u.user_id} u={u} right={<Button size="sm" variant="outline" onClick={() => unfriend(u)} data-testid={`unfriend-${u.user_id}`}>{id ? "Hapus" : "Remove"}</Button>} />)}
          </div>
        ) : <EmptyState icon={Users} title={id ? "Teman" : "Friends"} description={id ? "Belum punya teman. Cari orang untuk ditambahkan!" : "No friends yet. Find people to add!"} />)
      )}

      <UserProfileCard userId={cardId} open={!!cardId} onClose={() => setCardId(null)} />
    </div>
  );
}
